import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { startManagerLabelDefaults } from './managerLabelDefaults';
import en from '../../locales/en';

type LocaleModule = {
    default: Record<string, unknown>;
};

const localeLoaders = import.meta.glob('../../locales/*/index.ts');

const supportedLanguages = Object.keys(localeLoaders)
    .map((path) => path.split('/')[3])
    .filter((language): language is string => Boolean(language));

const resources = {
    en: {
        translation: en,
    },
};

/**
 * Language-specific fallback chains.
 * When a key is missing in the primary language, i18next tries each fallback in order.
 * The corresponding locale bundles are preloaded alongside the primary one so i18next
 * can resolve them without an additional async fetch.
 */
const FALLBACK_CHAINS: Record<string, string[]> = {
    fr_FR: ['fr', 'en'],
    fr: ['fr_FR', 'en'],
    pt_BR: ['pt', 'en'],
    pt: ['pt_BR', 'en'],
    zh_Hant: ['zh', 'en'],
};

const fallbackLngConfig: Record<string, string[]> = {
    ...FALLBACK_CHAINS,
    default: ['en'],
};

const loadBundle = async (language: string) => {
    if (i18n.hasResourceBundle(language, 'translation')) {
        return;
    }
    const loader = localeLoaders[`../../locales/${language}/index.ts`];
    if (!loader) {
        return;
    }
    const module = (await loader()) as LocaleModule;
    i18n.addResourceBundle(language, 'translation', module.default, true, true);
};

const loadLocaleResource = async (language: string) => {
    await loadBundle(language);

    // Preload fallback bundles so i18next can resolve missing keys without extra fetches
    const chain = FALLBACK_CHAINS[language];
    if (chain) {
        await Promise.all(chain.filter((lang) => lang !== 'en').map(loadBundle));
    }
};

// Initialize with a default language for SSR
i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: 'en',
        fallbackLng: fallbackLngConfig,
        interpolation: {
            escapeValue: false,
        },
    });

const originalChangeLanguage = i18n.changeLanguage.bind(i18n);

i18n.changeLanguage = (async (
    language: string,
    callback?: (error: unknown, t: typeof i18n.t) => void,
) => {
    await loadLocaleResource(language);
    return originalChangeLanguage(language, callback);
}) as typeof i18n.changeLanguage;

// The company's own word for the role feeds every sentence that names it, from here on.
startManagerLabelDefaults(i18n);

/**
 * The browser's language, when the module has a bundle for it. Only a fallback for when the host
 * transmits none: the transmitted language is the account's choice and always wins.
 */
export const browserLanguage = (): string | undefined => {
    const languageCode = navigator.language.split('-')[0];
    return supportedLanguages.includes(languageCode) ? languageCode : undefined;
};

export default i18n;
